import React, { useMemo } from "react";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import {
  Form,
  Button,
  Row,
  Col,
  Modal
} from "react-bootstrap";
import AppInputField from "components/shared/FormComponents/AppInputField";
import { useDispatch } from "react-redux";
import { addNewModule, editModule, getModulesList } from "store/services/emailPreferenceService";
import { STATUS_ENUM } from "common/constants";

const AddNewModulePopup = ({ showPopup, closePopup, data }) => {
  const dispatch = useDispatch();

  const initialValues = useMemo(() => {
    return {
      type: "module",
      name: data?.module_name || "",
      slug: data?.slug || "",
      description: data?.description || "",
      status: data?.status || "active",
    }
  }, [data])

  const formik = useFormik({
    initialValues,
    validationSchema: Yup.object({
      name: Yup.string().required("Module name is required"),
      slug: Yup.string()
        .matches(/^[a-z0-9_]+$/, "Only lowercase letters, numbers and underscores allowed")
        .required("Slug is required"),
      description: Yup.string().required("Description is required"),
      status: Yup.string().required("Status is required"),
    }),
    onSubmit: async (values, { resetForm, setSubmitting }) => {
      try {
        if(!data?.module_id) {
          await dispatch(addNewModule(values));
        } else {
          const payload = {
            id: data?.module_id,
            values,
          }

          await dispatch(editModule(payload));
        }
        await dispatch(getModulesList());
        resetForm();
        closePopup();
      } catch (err) {
        console.error("Error adding new module", err);
      } finally {
        setSubmitting(false);
      }
    },
    enableReinitialize: true,
  });

  const handleClose = () => {
    closePopup();
  }

  return (
    <Modal
      show={showPopup}
      onHide={handleClose}
      backdrop="static"
      keyboard={false}
      centered
      size="md"
    >
      <Modal.Header closeButton>
        <Modal.Title>{data?.module_id ? "Edit" : "Add"} Module</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <FormikProvider value={formik}>
          <Form onSubmit={formik.handleSubmit}>
            <Row className="align-items-end">
              <Col md={12} className="my-2">
                <div className="form-group m-0">
                  <label>Module Name</label>
                  <AppInputField
                    name="name"
                    placeholder="Enter module name"
                  />
                </div>
              </Col>
              <Col md={12} className="my-2">
                <div className="form-group m-0">
                  <label>Slug</label>
                  <AppInputField
                    name="slug"
                    placeholder="Enter module slug"
                    disabled={!!data?.module_id}
                  />
                </div>
              </Col>
              <Col md={12} className="my-2">
                <div className="form-group m-0">
                  <Form.Label>Description</Form.Label>
                  <AppInputField
                    name="description"
                    fieldType="textarea"
                    placeholder="Short description of this module"
                  />
                </div>
              </Col>
              <Col md={12} className="my-2">
                <div className="form-group m-0">
                  <Form.Label>Status</Form.Label>
                  <div className="d-flex gap-10 align-items-center">
                    {STATUS_ENUM.map((option, index) => (
                      <div className="form-group d-flex align-items-center" key={index}>
                        <input
                          type="radio"
                          className="form-radio"
                          name="status"
                          onChange={(e) => formik.setFieldValue("status", option.value)}
                          value={formik.values.status}
                          checked={formik.values.status === option.value}
                        />
                        <label className="m-0">&nbsp;{option.label}</label>
                      </div>
                    ))}
                  </div>
                </div>
              </Col>
              <Col md={12} className="d-flex justify-content-end gap-10">
                <Button
                  type="button"
                  variant="secondary"
                  className="mt-3"
                  onClick={handleClose}
                >
                  Close
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={formik.isSubmitting}
                  className="mt-3"
                >
                  {formik.isSubmitting ? "Please wait..." : data?.module_id ? "Update Module" : "Add Module"}
                </Button>
              </Col>
            </Row>
          </Form>
        </FormikProvider>
      </Modal.Body>
    </Modal>
  );
};

export default AddNewModulePopup;
